import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { fetchRest } from '@/common/api/fetcher'
import { ugcDisplayRuleEditPath, ugcDisplayRuleNewPath } from '@/common/app/path'
import { UGCDisplayRule } from '@/common/model/ugcDisplay'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Area from '@/components/view/layouts/area/Area'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const TH =
  'text-secondary border-b-2 border-secondary px-2.5 py-2 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-2 align-middle'

const CONTENT_TYPE_LABELS: Record<string, string> = {
  review: 'レビューのみ',
  ugc: 'レビュー以外',
  mixed: '混在'
}

const LAYOUT_LABELS: Record<string, string> = {
  feed: '縦に並べる',
  grid: '格子',
  carousel: '横スクロール',
  modal: 'モーダル',
  story: 'ストーリー'
}

function embedTag(placementKey: string) {
  return `<div class="w2ugc" data-placement="${placementKey}" data-product-id="{商品ID}"></div>`
}

export default function UGCDisplayRules() {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const [rules, setRules] = useState<UGCDisplayRule[]>([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState('')

  const fetchRules = () => {
    setLoading(true)
    fetchRest<{ rules?: UGCDisplayRule[] }>(`/api/sites/${siteId}/ugc/display-rules`)
      .then((d) => setRules(d.rules || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchRules()
  }, [siteId])

  const handleDelete = (rule: UGCDisplayRule) => {
    if (!confirm(`「${rule.rule_name}」を削除しますか？`)) return
    fetchRest(`/api/sites/${siteId}/ugc/display-rules/${rule.id}`, { method: 'DELETE' }).then(() =>
      fetchRules()
    )
  }

  const handleToggle = (rule: UGCDisplayRule) => {
    fetchRest(`/api/sites/${siteId}/ugc/display-rules/${rule.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: !rule.is_active })
    }).then(() => fetchRules())
  }

  const copyTag = (key: string) => {
    navigator.clipboard.writeText(embedTag(key))
    setCopied(key)
    setTimeout(() => setCopied(''), 2000)
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🎯 サイト表示枠設定'
        description='枠（設置タグから指定して表示する単位）ごとに、出す内容とデザインを設定します'
        rightNode={
          <Button
            color={ButtonColor.PRIMARY}
            onClick={() => navigate(ugcDisplayRuleNewPath(Number(siteId)))}
          >
            + 枠を追加
          </Button>
        }
      />

      <Content>
        <Area>
          <div className='overflow-x-auto'>
            <table className='w-full border-collapse text-xs'>
              <thead>
                <tr className='bg-surface-secondary'>
                  <th className={TH}>優先度</th>
                  <th className={TH}>枠の識別子</th>
                  <th className={TH}>枠の名前</th>
                  <th className={TH}>種類</th>
                  <th className={TH}>レイアウト</th>
                  <th className={TH}>表示</th>
                  <th className={TH}>状態</th>
                  <th className={TH}>操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className='text-tertiary p-10 text-center'>
                      読み込み中...
                    </td>
                  </tr>
                ) : rules.length === 0 ? (
                  <tr>
                    <td colSpan={8} className='text-tertiary p-10 text-center'>
                      枠がありません。「+ 枠を追加」で作成してください。
                    </td>
                  </tr>
                ) : (
                  rules.map((r) => (
                    <tr key={r.id} className='border-secondary border-b'>
                      <td className={`${TD} text-emphasis font-bold`}>{r.priority}</td>
                      <td className={TD}>
                        {r.placement_key ? (
                          <div className='flex items-center gap-1.5'>
                            <code className='bg-surface-emphasis text-emphasis rounded px-1.5 py-0.5 text-[10px]'>
                              {r.placement_key}
                            </code>
                            <button
                              onClick={() => copyTag(r.placement_key as string)}
                              className='text-emphasis cursor-pointer text-[10px]'
                            >
                              {copied === r.placement_key ? 'コピー済' : 'タグ'}
                            </button>
                          </div>
                        ) : (
                          <span className='text-tertiary text-[10px]'>枠なし（従来タグ）</span>
                        )}
                      </td>
                      <td className={`${TD} font-semibold`}>{r.rule_name}</td>
                      <td className={`${TD} text-[10px]`}>
                        {CONTENT_TYPE_LABELS[r.content_type] || r.content_type}
                      </td>
                      <td className={TD}>
                        <span className='bg-surface-emphasis text-emphasis rounded-md px-2 py-0.5 text-[10px] font-semibold'>
                          {(r.template_type && LAYOUT_LABELS[r.template_type]) || r.template_type}
                        </span>
                      </td>
                      <td className={`${TD} text-secondary text-[10px]`}>
                        {r.ui_config?.expand_mode === 'overlay' ? 'モーダル' : 'ページ内追記'}
                        {r.ui_config?.initial_limit ? ` / 初期${r.ui_config.initial_limit}件` : ''}
                      </td>
                      <td className={TD}>
                        <span
                          onClick={() => handleToggle(r)}
                          className={`cursor-pointer rounded-[10px] px-2 py-0.5 text-[10px] font-semibold ${
                            r.is_active
                              ? 'bg-surface-success text-success'
                              : 'bg-surface-secondary text-secondary'
                          }`}
                        >
                          {r.is_active ? 'ON' : 'OFF'}
                        </span>
                      </td>
                      <td className={TD}>
                        <div className='flex gap-2'>
                          <button
                            onClick={() => navigate(ugcDisplayRuleEditPath(Number(siteId), r.id))}
                            className='text-emphasis cursor-pointer text-[11px]'
                          >
                            編集
                          </button>
                          <button
                            onClick={() => handleDelete(r)}
                            className='text-critical cursor-pointer text-[11px]'
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Area>
      </Content>
    </Main>
  )
}
